import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useRecoilState} from 'recoil';
import {makeRequest} from '../../utils/httpRequest';
import Config from '../../core/config';
import {loadingAtom} from '../../atoms';
import {UserPermission, CompanyPermission} from '../../utils/utils';
import {getCurrentUserId} from '../../utils/auth';
import Loading from '../../core/Loading';
import {usePermission} from '../../core/PermissionContext';
import NewButton from '../common/NewButton';
import TableRowActions from '../common/TableRowActions';
import ListPageLayout from '../common/ListPageLayout';
import ShoppingListShareDialog from './ShoppingListShareDialog';
import ShoppingListRepeatDialog from './ShoppingListRepeatDialog';
import './shoppingList.css';
import {getDefaultShoppingListName} from './shoppingListUtils';
import {showConfirm, showAlert} from '../../core/dialog';

const apiUrl = Config.apiUrl;

const ShoppingListList = () => {
    const {hasUserPermission, hasCompanyPermission, loadingPermissions} = usePermission();
    const navigate = useNavigate();
    const [lists, setLists] = useState([]);
    const [showHidden, setShowHidden] = useState(false);
    const [companyUsers, setCompanyUsers] = useState([]);
    const [shareList, setShareList] = useState(null);
    const [repeatSource, setRepeatSource] = useState(null);
    const [, setLoading] = useRecoilState(loadingAtom);
    const currentUserId = getCurrentUserId();

    const canList = () =>
        hasUserPermission(UserPermission.LIST_SHOPPING_LIST)
        && hasCompanyPermission(CompanyPermission.LIST_SHOPPING_LIST);

    const canCreate = () =>
        hasUserPermission(UserPermission.CREATE_SHOPPING_LIST)
        && hasCompanyPermission(CompanyPermission.CREATE_SHOPPING_LIST);

    const canEdit = () =>
        hasUserPermission(UserPermission.EDIT_SHOPPING_LIST)
        && hasCompanyPermission(CompanyPermission.EDIT_SHOPPING_LIST);

    const canRemove = () =>
        hasUserPermission(UserPermission.REMOVE_SHOPPING_LIST)
        && hasCompanyPermission(CompanyPermission.REMOVE_SHOPPING_LIST);

    const isListOwner = (list) => {
        if (currentUserId == null || list?.ownerUserId == null) {
            return false;
        }
        return Number(list.ownerUserId) === Number(currentUserId);
    };

    async function loadCompanyUsers() {
        try {
            const users = await makeRequest(`${apiUrl}/api/users/company/query=`, 'GET');
            setCompanyUsers(users || []);
        } catch (error) {
            console.error('Error loading company users:', error);
        }
    }

    async function loadLists(includeHidden = showHidden) {
        try {
            setLoading(true);
            const response = await makeRequest(
                `${apiUrl}/api/shopping-lists?includeHidden=${includeHidden}`,
                'GET',
            );
            setLists(response || []);
        } catch (error) {
            console.error('Error fetching shopping lists:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!loadingPermissions && canList()) {
            loadLists();
            loadCompanyUsers();
        }
    }, [loadingPermissions, showHidden]);

    async function removeList(id) {
        try {
            setLoading(true);
            await makeRequest(`${apiUrl}/api/shopping-lists/${id}`, 'DELETE');
            await loadLists();
        } catch (error) {
            console.error('Error deleting shopping list:', error);
        } finally {
            setLoading(false);
        }
    }

    async function setListDefaultTemplate(listId, defaultTemplate) {
        try {
            setLoading(true);
            await makeRequest(
                `${apiUrl}/api/shopping-lists/${listId}/default-template?defaultTemplate=${defaultTemplate}`,
                'PATCH',
            );
            await loadLists();
        } catch (error) {
            console.error('Error updating default template flag:', error);
            showAlert('Erro ao atualizar a lista padrão.');
        } finally {
            setLoading(false);
        }
    }

    async function setListHidden(listId, hidden) {
        try {
            setLoading(true);
            await makeRequest(
                `${apiUrl}/api/shopping-lists/${listId}/hidden?hidden=${hidden}`,
                'PATCH',
            );
            await loadLists();
        } catch (error) {
            console.error('Error updating shopping list visibility:', error);
            showAlert(hidden ? 'Erro ao concluir a lista.' : 'Erro ao reabrir a lista.');
        } finally {
            setLoading(false);
        }
    }

    async function saveSharedUsers(listId, sharedUserIds) {
        try {
            setLoading(true);
            await makeRequest(
                `${apiUrl}/api/shopping-lists/${listId}/shared-users`,
                'PATCH',
                {'Content-Type': 'application/json'},
                JSON.stringify(sharedUserIds || []),
            );
            setShareList(null);
            await loadLists();
        } catch (error) {
            console.error('Error updating shopping list sharing:', error);
            showAlert('Erro ao compartilhar a lista.');
        } finally {
            setLoading(false);
        }
    }

    function buildRepeatItems(sourceItems) {
        return (sourceItems || [])
            .filter((item) => item.itemName && item.itemName.trim() !== '')
            .map((item, index) => ({
                itemName: item.itemName.trim(),
                quantity: String(item.quantity || '1'),
                observation: item.observation || '',
                supermarketSector: item.supermarketSector || '',
                purchased: false,
                sortOrder: index,
            }));
    }

    async function repeatList(listId) {
        try {
            setLoading(true);
            const source = await makeRequest(`${apiUrl}/api/shopping-lists/id=${listId}`, 'GET');
            setRepeatSource(source);
        } catch (error) {
            console.error('Error loading shopping list to repeat:', error);
            showAlert('Erro ao carregar a lista para repetir.');
        } finally {
            setLoading(false);
        }
    }

    async function confirmRepeatList(name) {
        if (!repeatSource) {
            return;
        }
        try {
            setLoading(true);
            const payload = {
                name,
                sharedUserIds: repeatSource.sharedUserIds || [],
                items: buildRepeatItems(repeatSource.items),
            };
            const saved = await makeRequest(
                `${apiUrl}/api/shopping-lists`,
                'POST',
                {'Content-Type': 'application/json'},
                JSON.stringify(payload),
            );
            setRepeatSource(null);
            if (saved?.id) {
                navigate(`/app/shoppinglist?id=${saved.id}`);
            }
        } catch (error) {
            console.error('Error creating repeated shopping list:', error);
            showAlert('Erro ao criar a cópia da lista.');
        } finally {
            setLoading(false);
        }
    }

    const formatDate = (value) => {
        if (!value) return '-';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value;
        return d.toLocaleString('pt-BR', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'});
    };

    if (loadingPermissions) return <Loading />;
    if (!canList()) {
        navigate('/unauthorizedaccess');
        return null;
    }

    const getActions = (list) => {
        const actions = [
            {
                key: 'edit',
                label: 'Editar',
                type: 'edit',
                visible: canEdit(),
                onClick: () => navigate(`/app/shoppinglist?id=${list.id}`),
            },
            {
                key: 'repeat',
                label: 'Repetir',
                type: 'new',
                visible: canCreate(),
                onClick: () => repeatList(list.id),
            },
        ];

        if (canEdit() && isListOwner(list)) {
            actions.push({
                key: 'share',
                label: 'Compartilhar',
                visible: true,
                onClick: () => setShareList(list),
            });
            actions.push({
                key: 'default-template',
                label: list.defaultTemplate ? 'Remover padrão' : 'Definir como padrão',
                visible: true,
                onClick: () => setListDefaultTemplate(list.id, !list.defaultTemplate),
            });
        }

        if (canEdit() && list.hidden) {
            actions.push({
                key: 'reopen',
                label: 'Reabrir',
                type: 'reverse',
                visible: true,
                onClick: () => setListHidden(list.id, false),
            });
        } else if (canEdit() && !list.hidden && isListOwner(list)) {
            actions.push({
                key: 'complete',
                label: 'Concluir',
                type: 'confirm',
                visible: true,
                onClick: async () => {
                    if (await showConfirm('Deseja concluir esta lista?')) {
                        setListHidden(list.id, true);
                    }
                },
            });
        }

        actions.push({
            key: 'delete',
            label: 'Excluir',
            type: 'delete',
            visible: canRemove(),
            onClick: async () => {
                if (await showConfirm('Deseja realmente excluir esta lista?')) {
                    removeList(list.id);
                }
            },
            buttonProps: {className: 'remove-button'},
        });

        return actions;
    };

    return (
        <ListPageLayout
            tableWidth={720}
            header={
                <div style={{display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
                    <div className="header-label">Listas de Compras</div>
                    {canCreate() && (
                        <NewButton className="new-button" onClick={() => navigate('/app/shoppinglist')}/>
                    )}
                </div>
            }
        >
            <div className="shopping-list-list-toolbar">
                <label className="shopping-list-filter-completed">
                    <input
                        type="checkbox"
                        checked={showHidden}
                        onChange={(e) => setShowHidden(e.target.checked)}
                        data-test="shopping-list-show-completed"
                    />
                    Mostrar concluídas
                </label>
            </div>
            <div className="shopping-list-cards">
                {lists.map((list) => (
                    <div
                        key={list.id}
                        className={`shopping-list-card${list.hidden ? ' shopping-list-card--completed' : ''}`}
                    >
                        <div className="shopping-list-card__info">
                            <div className="shopping-list-card__title">
                                {list.name}
                                {list.defaultTemplate && (
                                    <span className="shopping-list-card__badge shopping-list-card__badge--default">
                                        Padrão
                                    </span>
                                )}
                                {list.hidden && (
                                    <span className="shopping-list-card__badge">Concluída</span>
                                )}
                            </div>
                            <div className="shopping-list-card__meta">
                                <span>{(list.items || []).length} itens</span>
                                {(list.sharedUserIds || []).length > 0 && (
                                    <span>Compartilhada ({list.sharedUserIds.length})</span>
                                )}
                            </div>
                            <div className="shopping-list-card__date">
                                Atualizada: {formatDate(list.updatedAt)}
                            </div>
                        </div>
                        <div className="shopping-list-card__actions">
                            <TableRowActions actions={getActions(list)}/>
                        </div>
                    </div>
                ))}
            </div>
            {lists.length === 0 && (
                <div className="shopping-list-empty">Nenhuma lista encontrada.</div>
            )}
            <ShoppingListShareDialog
                open={!!shareList}
                list={shareList}
                companyUsers={companyUsers}
                currentUserId={currentUserId}
                onClose={() => setShareList(null)}
                onSave={(sharedUserIds) => {
                    if (shareList?.id) {
                        saveSharedUsers(shareList.id, sharedUserIds);
                    }
                }}
            />
            <ShoppingListRepeatDialog
                open={!!repeatSource}
                sourceList={repeatSource}
                defaultName={getDefaultShoppingListName()}
                onClose={() => setRepeatSource(null)}
                onConfirm={confirmRepeatList}
            />
        </ListPageLayout>
    );
};

export default ShoppingListList;
